import { Injectable } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult /*, InteractionStatus*/ } from '@azure/msal-browser';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, shareReplay } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _user$ = new BehaviorSubject<AccountInfo>(undefined);
  public readonly user$ = this._user$.asObservable().pipe(
    shareReplay({
      refCount: true,
      bufferSize: 1,
    })
  );

  private readonly _isLoading$ = new BehaviorSubject<boolean>(false);
  public readonly isLoading$ = this._isLoading$.asObservable().pipe(
    shareReplay({
      refCount: true,
      bufferSize: 1,
    })
  );

  private readonly authResult$ = new BehaviorSubject<AuthenticationResult>(undefined);
  public readonly idToken$: Observable<string> = this.authResult$.asObservable().pipe(
    shareReplay({
      refCount: true,
      bufferSize: 1,
    }),
    filter((authResult) => !!authResult),
    filter((authResult) => {
      // if we catch expired token on client side, we prevent using this token, also refresh it

      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      const addMinutes = (date: Date, minutes: number) => new Date(date.getTime() + minutes * 60000);

      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      const fiveMinutesFromNow = addMinutes(new Date(), 5);
      if (authResult.expiresOn <= fiveMinutesFromNow) {
        this.refreshToken().catch((e) => console.error(e));
        return false;
      }
      return true;
    }),
    map((authResult) => authResult.idToken)
  );

  constructor(private readonly msalService: MsalService, private readonly msalBroadcastService: MsalBroadcastService) {
    // eslint-disable-next-line rxjs/no-async-subscribe
    this.msalBroadcastService.inProgress$.subscribe(async (status) => {
      /*
      switch (status) {
        case InteractionStatus.None:
          await this.acquireToken();
          this._isLoading$.next(false);
          break;
        default:
          this._isLoading$.next(true);
          break;
      }
      */
      await this.acquireToken();
      this._isLoading$.next(false);
    });
  }

  public refreshToken(forceRefresh?: boolean) {
    return this.acquireToken(forceRefresh);
  }

  public logout() {
    this.msalService.logout();
  }

  private acquireToken(forceRefresh?: boolean): Promise<void | AuthenticationResult> {
    const accounts = this.msalService.instance.getAllAccounts();
    const activeAccount = accounts[0];
    if (accounts.length > 1) {
      console.warn('There is more than one account, selecting the first:', JSON.stringify(activeAccount));
    }
    this.msalService.instance.setActiveAccount(activeAccount);
    this._user$.next(activeAccount);

    if (!activeAccount) {
      return;
    }

    return this.msalService.instance
      .acquireTokenSilent({ scopes: ['User.Read'], account: activeAccount, forceRefresh })
      .then((authResult) => {
        this.authResult$.next(authResult);
      })
      .catch((error) => {
        console.error(error);
        // No tokens were found for the given scopes, so we are logging the user out forcefully
        this.logout();
      });
  }
}
